/**
 * Road graph queries: routing, and finding things by position.
 *
 * DESIGN.md section 5: vehicles move on the graph rather than in free 2D
 * space, so pathfinding is graph pathfinding, detection is "traversed node N",
 * and route reconstruction is a query over edges and timestamps rather than a
 * geometry problem. This file is the graph half of that bargain.
 */

import { type County, edgeEnterableFrom, otherNode } from './county.ts';
import {
  AVOID_EDGE_PENALTY,
  DISTANCE_COST_SECONDS_PER_METER,
  ROAD_CLASS_SPEED,
} from './constants.ts';

/**
 * Binary min-heap keyed by a float priority, storing int payloads.
 *
 * Hand-rolled because @hotlist/shared takes no dependencies, and because a
 * pair of typed arrays beats an array of objects when the router runs for
 * every vehicle that finishes a trip.
 */
class MinHeap {
  private keys: Float64Array;
  private values: Int32Array;
  private size = 0;

  constructor(capacity: number) {
    this.keys = new Float64Array(Math.max(16, capacity));
    this.values = new Int32Array(Math.max(16, capacity));
  }

  get length(): number {
    return this.size;
  }

  clear(): void {
    this.size = 0;
  }

  push(key: number, value: number): void {
    if (this.size === this.keys.length) this.grow();
    let i = this.size++;
    this.keys[i] = key;
    this.values[i] = value;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if ((this.keys[parent] as number) <= (this.keys[i] as number)) break;
      this.swap(i, parent);
      i = parent;
    }
  }

  /** Returns the payload of the smallest key, or -1 when empty. */
  pop(): number {
    if (this.size === 0) return -1;
    const top = this.values[0] as number;
    this.size--;
    if (this.size > 0) {
      this.keys[0] = this.keys[this.size] as number;
      this.values[0] = this.values[this.size] as number;
      let i = 0;
      for (;;) {
        const left = i * 2 + 1;
        const right = left + 1;
        let smallest = i;
        if (left < this.size && (this.keys[left] as number) < (this.keys[smallest] as number)) {
          smallest = left;
        }
        if (right < this.size && (this.keys[right] as number) < (this.keys[smallest] as number)) {
          smallest = right;
        }
        if (smallest === i) break;
        this.swap(i, smallest);
        i = smallest;
      }
    }
    return top;
  }

  private swap(i: number, j: number): void {
    const k = this.keys[i] as number;
    this.keys[i] = this.keys[j] as number;
    this.keys[j] = k;
    const v = this.values[i] as number;
    this.values[i] = this.values[j] as number;
    this.values[j] = v;
  }

  private grow(): void {
    const keys = new Float64Array(this.keys.length * 2);
    keys.set(this.keys);
    this.keys = keys;
    const values = new Int32Array(this.values.length * 2);
    values.set(this.values);
    this.values = values;
  }
}

/**
 * Scratch buffers for the router, allocated once per Router rather than per
 * call. A Router is not reentrant and is not safe to share across concurrent
 * routing calls; the sim uses exactly one and routes one vehicle at a time.
 */
export class Router {
  private readonly county: County;
  private readonly cost: Float64Array;
  private readonly cameFromEdge: Int32Array;
  private readonly visitGen: Int32Array;
  private readonly closedGen: Int32Array;
  private readonly heap: MinHeap;
  private readonly fastest: number;
  private generation = 0;

  constructor(county: County) {
    this.county = county;
    this.cost = new Float64Array(county.nodeCount);
    this.cameFromEdge = new Int32Array(county.nodeCount);
    this.visitGen = new Int32Array(county.nodeCount);
    this.closedGen = new Int32Array(county.nodeCount);
    this.heap = new MinHeap(1024);
    // Admissible heuristic needs the fastest speed anything can travel at.
    let fastest = 1;
    for (const speed of Object.values(ROAD_CLASS_SPEED)) fastest = Math.max(fastest, speed);
    this.fastest = fastest;
  }

  /**
   * Shortest route by travel time from `from` to `to`, as the sequence of
   * edges to traverse. Empty when from equals to. Null when the destination is
   * unreachable, which happens on a graph with disconnected fragments.
   */
  route(from: number, to: number, avoid?: ReadonlySet<number>): number[] | null {
    const county = this.county;
    if (from === to) return [];
    if (from < 0 || from >= county.nodeCount || to < 0 || to >= county.nodeCount) return null;

    const gen = ++this.generation;
    this.heap.clear();

    const targetX = county.nodeX[to] as number;
    const targetY = county.nodeY[to] as number;
    // Admissible against the cost above: the cheapest any meter can be is the
    // time at the fastest speed plus its distance charge. Leaving the distance
    // term out would still be admissible, but including it keeps the search
    // tight now that the cost is larger.
    const perMeter = 1 / this.fastest + DISTANCE_COST_SECONDS_PER_METER;
    const heuristic = (n: number): number => {
      const dx = (county.nodeX[n] as number) - targetX;
      const dy = (county.nodeY[n] as number) - targetY;
      return Math.hypot(dx, dy) * perMeter;
    };

    this.cost[from] = 0;
    this.cameFromEdge[from] = -1;
    this.visitGen[from] = gen;
    this.heap.push(heuristic(from), from);

    while (this.heap.length > 0) {
      const node = this.heap.pop();
      if (node < 0) break;
      if (this.closedGen[node] === gen) continue;
      this.closedGen[node] = gen;

      if (node === to) return this.reconstruct(from, to);

      const start = county.adjStart[node] as number;
      const end = county.adjStart[node + 1] as number;
      const here = this.cost[node] as number;

      for (let i = start; i < end; i++) {
        const edge = county.adjEdge[i] as number;
        if (!edgeEnterableFrom(county, edge, node)) continue;
        const next = otherNode(county, edge, node);
        if (this.closedGen[next] === gen) continue;
        // Roads the traveller would rather not be seen on cost more, they are
        // not forbidden. A hard ban would make some destinations unreachable
        // and would let a player discover coverage by watching routing fail;
        // a penalty makes avoidance a decision with a price, which is what it
        // is. The multiplier is large enough that a detour of several minutes
        // beats a watched approach, and small enough that a road you have no
        // choice about is still taken.
        const penalty = avoid?.has(edge) ? AVOID_EDGE_PENALTY : 1;
        // Time, plus what the distance itself costs. Without the second term a
        // router takes any detour that saves a second, and every trip becomes a
        // motorway trip; see DISTANCE_COST_SECONDS_PER_METER.
        const cost =
          (county.edgeTime[edge] as number) +
          (county.edgeLen[edge] as number) * DISTANCE_COST_SECONDS_PER_METER;
        const candidate = here + cost * penalty;
        if (this.visitGen[next] === gen && (this.cost[next] as number) <= candidate) continue;
        this.visitGen[next] = gen;
        this.cost[next] = candidate;
        this.cameFromEdge[next] = edge;
        this.heap.push(candidate + heuristic(next), next);
      }
    }

    return null;
  }

  private reconstruct(from: number, to: number): number[] {
    const county = this.county;
    const edges: number[] = [];
    let node = to;
    // The graph has at most edgeCount edges in any simple path; the bound
    // turns a corrupt predecessor chain into an exception rather than a hang.
    for (let guard = 0; guard <= county.edgeCount; guard++) {
      if (node === from) {
        edges.reverse();
        return edges;
      }
      const edge = this.cameFromEdge[node] as number;
      if (edge < 0) break;
      edges.push(edge);
      node = otherNode(county, edge, node);
    }
    throw new Error('Router.reconstruct walked off the predecessor chain');
  }
}

/**
 * Uniform grid over the county for "what is near this point" questions.
 * Cell size is chosen so a typical cell holds a handful of nodes.
 */
export interface SpatialIndex {
  readonly cellSize: number;
  readonly cols: number;
  readonly rows: number;
  readonly minX: number;
  readonly minY: number;
  readonly start: Int32Array;
  readonly items: Int32Array;
}

export function buildNodeIndex(county: County, cellSize = 400): SpatialIndex {
  const { minX, minY, maxX, maxY } = county.bounds;
  const cols = Math.max(1, Math.ceil((maxX - minX) / cellSize));
  const rows = Math.max(1, Math.ceil((maxY - minY) / cellSize));
  const cellOf = (n: number): number => {
    const cx = clamp(Math.floor(((county.nodeX[n] as number) - minX) / cellSize), 0, cols - 1);
    const cy = clamp(Math.floor(((county.nodeY[n] as number) - minY) / cellSize), 0, rows - 1);
    return cy * cols + cx;
  };

  const counts = new Int32Array(cols * rows);
  for (let n = 0; n < county.nodeCount; n++) {
    const cell = cellOf(n);
    counts[cell] = (counts[cell] as number) + 1;
  }

  const start = new Int32Array(cols * rows + 1);
  for (let c = 0; c < cols * rows; c++) start[c + 1] = (start[c] as number) + (counts[c] as number);

  const items = new Int32Array(county.nodeCount);
  const cursor = Int32Array.from(start.subarray(0, cols * rows));
  for (let n = 0; n < county.nodeCount; n++) {
    const cell = cellOf(n);
    items[cursor[cell] as number] = n;
    cursor[cell] = (cursor[cell] as number) + 1;
  }

  return { cellSize, cols, rows, minX, minY, start, items };
}

/**
 * Nearest node to a point, searching outward in rings of cells and stopping
 * once no unexamined ring can beat the best found so far. Returns -1 only when
 * the index is empty.
 */
export function nearestNode(
  county: County,
  index: SpatialIndex,
  x: number,
  y: number,
): number {
  const cx = clamp(Math.floor((x - index.minX) / index.cellSize), 0, index.cols - 1);
  const cy = clamp(Math.floor((y - index.minY) / index.cellSize), 0, index.rows - 1);

  let best = -1;
  let bestDistanceSq = Infinity;
  const maxRing = Math.max(index.cols, index.rows);

  for (let ring = 0; ring <= maxRing; ring++) {
    // Once a full ring has been examined, any node in a further ring is at
    // least (ring * cellSize) away, so a closer hit is impossible.
    if (best >= 0) {
      const floor = (ring - 1) * index.cellSize;
      if (floor > 0 && floor * floor > bestDistanceSq) break;
    }

    for (let gy = cy - ring; gy <= cy + ring; gy++) {
      if (gy < 0 || gy >= index.rows) continue;
      for (let gx = cx - ring; gx <= cx + ring; gx++) {
        if (gx < 0 || gx >= index.cols) continue;
        // Interior cells were covered by an earlier ring.
        const onRing =
          ring === 0 || gx === cx - ring || gx === cx + ring || gy === cy - ring || gy === cy + ring;
        if (!onRing) continue;

        const cell = gy * index.cols + gx;
        const from = index.start[cell] as number;
        const to = index.start[cell + 1] as number;
        for (let i = from; i < to; i++) {
          const node = index.items[i] as number;
          const dx = (county.nodeX[node] as number) - x;
          const dy = (county.nodeY[node] as number) - y;
          const distanceSq = dx * dx + dy * dy;
          if (distanceSq < bestDistanceSq) {
            bestDistanceSq = distanceSq;
            best = node;
          }
        }
      }
    }
  }

  return best;
}

/**
 * Node ids of the largest connected component, which is the only part of the
 * map anything should be spawned on. A bake can leave small fragments behind
 * where a road was clipped at the county line, and a vehicle stranded on one
 * would sit there forever failing to route.
 */
export function largestComponent(county: County): Int32Array {
  const component = new Int32Array(county.nodeCount).fill(-1);
  const queue = new Int32Array(county.nodeCount);
  let bestId = -1;
  let bestSize = 0;
  let nextId = 0;

  for (let seed = 0; seed < county.nodeCount; seed++) {
    if (component[seed] !== -1) continue;
    const id = nextId++;
    let head = 0;
    let tail = 0;
    queue[tail++] = seed;
    component[seed] = id;
    let size = 0;
    while (head < tail) {
      const node = queue[head++] as number;
      size++;
      const start = county.adjStart[node] as number;
      const end = county.adjStart[node + 1] as number;
      for (let i = start; i < end; i++) {
        // Connectivity here ignores one-way, which is the right call: a
        // one-way street still connects the two ends of the network for a
        // driver willing to go around the block.
        const next = otherNode(county, county.adjEdge[i] as number, node);
        if (component[next] !== -1) continue;
        component[next] = id;
        queue[tail++] = next;
      }
    }
    if (size > bestSize) {
      bestSize = size;
      bestId = id;
    }
  }

  const members = new Int32Array(bestSize);
  let write = 0;
  for (let n = 0; n < county.nodeCount; n++) {
    if (component[n] === bestId) members[write++] = n;
  }
  return members;
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/**
 * How much of a route is left, in meters and in seconds.
 *
 * Computed on the client from the county it already holds, rather than sent.
 * The route is a list of edge ids and the county has every edge's length and
 * speed limit, so a second number on the wire would be a second copy of
 * something the client can already work out -- and one that could disagree with
 * the line drawn on the map.
 *
 * `index` is how far into the route the car has got. The edge it is *on* counts
 * only for the part still ahead of it, which is what stops the estimate
 * jumping by a whole edge every time a junction goes past.
 *
 * The time is free-flow: the limit, the whole way, no queue at the lights and
 * no slowing for corners. It is therefore optimistic, and honestly so -- a
 * satnav's estimate is the same kind of guess, and the alternative is
 * simulating the drive to tell somebody how long the drive will take.
 */
export function routeRemaining(
  county: County,
  route: readonly number[],
  index: number,
  /** How far along the current edge the car has traveled, in meters. */
  offset = 0,
): { meters: number; seconds: number } {
  let meters = 0;
  let seconds = 0;
  for (let i = Math.max(0, index); i < route.length; i++) {
    const edge = route[i];
    if (edge === undefined) continue;
    const length = county.edgeLen[edge] as number;
    // Only the part of the current edge still in front of the car.
    const left = i === index ? Math.max(0, length - offset) : length;
    const speed = (county.edgeSpeed[edge] as number) || 1;
    meters += left;
    seconds += left / speed;
  }
  return { meters, seconds };
}

/** A distance a person would say out loud. */
export function sayDistance(meters: number): string {
  const miles = meters / 1609.34;
  if (miles < 0.1) return `${Math.round(meters / 0.3048 / 10) * 10} ft`;
  return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`;
}

/** A duration a person would say out loud. */
export function sayDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  if (minutes < 1) return 'under a minute';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
}

/**
 * The junction a click on the map means.
 *
 * `nearestNode` finds the nearest *junction*, which is not the same thing and
 * was visibly wrong: a click halfway down a long straight is much closer to a
 * junction on the next street over than to either end of the road it landed
 * on, so tapping a road sent you somewhere else entirely.
 *
 * The road is what somebody pointed at. So this finds the nearest **edge**
 * first -- by perpendicular distance, which is what "on that road" means -- and
 * then takes whichever end of it the click was nearer. Two roads crossing at a
 * junction give the same answer either way; a click mid-block now gives the
 * road it is on rather than whatever happened to be closest as the crow flies.
 *
 * Searched through the node index rather than over every edge: an edge is found
 * via its endpoints, and widening the ring until something turns up costs a few
 * cells rather than the county's 53,481 edges.
 */
export function nearestRoadNode(
  county: County,
  index: SpatialIndex,
  x: number,
  y: number,
): number {
  let bestEdge = -1;
  let bestAway = Number.POSITIVE_INFINITY;
  let bestT = 0;

  const seen = new Set<number>();
  const consider = (node: number): void => {
    const from = county.adjStart[node] as number;
    const to = county.adjStart[node + 1] as number;
    for (let i = from; i < to; i++) {
      const edge = county.adjEdge[i] as number;
      if (seen.has(edge)) continue;
      seen.add(edge);

      const a = county.edgeA[edge] as number;
      const b = county.edgeB[edge] as number;
      const ax = county.nodeX[a] as number;
      const ay = county.nodeY[a] as number;
      const bx = county.nodeX[b] as number;
      const by = county.nodeY[b] as number;

      // Closest point on the segment, clamped to its ends.
      const dx = bx - ax;
      const dy = by - ay;
      const len2 = dx * dx + dy * dy;
      const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / len2)) : 0;
      const away = Math.hypot(x - (ax + dx * t), y - (ay + dy * t));
      if (away < bestAway) {
        bestAway = away;
        bestEdge = edge;
        bestT = t;
      }
    }
  };

  // Every junction in a widening ring, so the edges around the click are all
  // considered rather than only those touching the single nearest junction.
  for (const node of nodesAround(index, x, y)) consider(node);
  if (bestEdge < 0) return nearestNode(county, index, x, y);

  const a = county.edgeA[bestEdge] as number;
  const b = county.edgeB[bestEdge] as number;
  return bestT < 0.5 ? a : b;
}

/**
 * Junctions in the cells around a point, widening until something turns up.
 *
 * Uses the same grid `nearestNode` walks, so this costs a handful of cells
 * rather than the county's 44,704 junctions. Widened rather than fixed because
 * a county has empty quarters, and a fixed reach returns nothing out in the
 * fields and leaves the caller to cope.
 */
function nodesAround(index: SpatialIndex, x: number, y: number): number[] {
  const cx = clamp(Math.floor((x - index.minX) / index.cellSize), 0, index.cols - 1);
  const cy = clamp(Math.floor((y - index.minY) / index.cellSize), 0, index.rows - 1);
  const maxRing = Math.max(index.cols, index.rows);

  const found: number[] = [];
  for (let ring = 0; ring <= maxRing; ring++) {
    for (let gy = cy - ring; gy <= cy + ring; gy++) {
      if (gy < 0 || gy >= index.rows) continue;
      for (let gx = cx - ring; gx <= cx + ring; gx++) {
        if (gx < 0 || gx >= index.cols) continue;
        const cell = gy * index.cols + gx;
        const from = index.start[cell] as number;
        const to = index.start[cell + 1] as number;
        for (let i = from; i < to; i++) found.push(index.items[i] as number);
      }
    }
    // One ring past the first hit, so an edge reaching in from a neighboring
    // cell is considered rather than missed by a meter.
    if (found.length > 0 && ring >= 1) return found;
  }
  return found;
}
